import React, { useState, useEffect } from "react";
import { supabase } from "../config";

function Wall() {
  const [contributions, setContributions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("all"); // "all" or "team"
  const [filterTeam, setFilterTeam] = useState("");

  useEffect(() => {
    async function fetchData() {
      try {
        const { data: contribs } = await supabase
          .from("sritaru_contributions")
          .select("first_name, num_trees, dedicated_to, payment_status, year, created_at, sritaru_teams(team_name)")
          .order("created_at", { ascending: false });

        // Normalize team name from join
        const normalized = (contribs || []).map(c => ({
          ...c,
          team: c.sritaru_teams?.team_name || null,
        }));
        setContributions(normalized);

        // Extract unique teams
        const uniqueTeams = [
          ...new Set(normalized.map((c) => c.team).filter(Boolean)),
        ].sort();
        setTeams(uniqueTeams);
      } catch (err) {
        console.error("Error fetching contributions:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const totalTrees = contributions.reduce(
    (sum, c) => sum + (c.num_trees || 0),
    0
  );

  const filtered =
    filterTeam
      ? contributions.filter((c) => c.team === filterTeam)
      : contributions;

  const grouped = {};
  if (viewMode === "team") {
    filtered.forEach((c) => {
      const t = c.team || "No Team";
      if (!grouped[t]) grouped[t] = [];
      grouped[t].push(c);
    });
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="py-10 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Hero card */}
        <div className="bg-gradient-to-r from-sritaru-600 to-sritaru-700 text-white rounded-xl shadow-lg p-8 mb-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">
            Contributor Wall &#127795;
          </h1>
          <p className="text-5xl font-bold mt-4">
            {totalTrees.toLocaleString()}
          </p>
          <p className="text-sritaru-200 text-lg">total trees contributed</p>
          <p className="text-sritaru-300 text-sm mt-2">
            {contributions.length} contributor
            {contributions.length !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6 items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode("all")}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                viewMode === "all"
                  ? "bg-sritaru-600 text-white"
                  : "bg-sritaru-100 text-sritaru-700 hover:bg-sritaru-200"
              }`}
            >
              All
            </button>
            <button
              onClick={() => setViewMode("team")}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                viewMode === "team"
                  ? "bg-sritaru-600 text-white"
                  : "bg-sritaru-100 text-sritaru-700 hover:bg-sritaru-200"
              }`}
            >
              By Team
            </button>
          </div>
          <select
            value={filterTeam}
            onChange={(e) => setFilterTeam(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-sritaru-400 outline-none"
          >
            <option value="">All Teams</option>
            {teams.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No contributions yet. Be the first!
          </div>
        ) : viewMode === "all" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((c, i) => (
              <div
                key={i}
                className="bg-white rounded-xl shadow-sm border border-sritaru-100 p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-2">
                  <p className="font-semibold text-sritaru-800">
                    {c.first_name}
                  </p>
                  {c.team && (
                    <span className="bg-sritaru-100 text-sritaru-700 text-xs px-2 py-1 rounded-full font-medium">
                      {c.team}
                    </span>
                  )}
                </div>
                <p className="text-gray-700 text-sm">
                  contributed{" "}
                  <span className="font-bold text-sritaru-600">
                    {c.num_trees}
                  </span>{" "}
                  &#127795;
                </p>
                {c.dedicated_to && (
                  <p className="text-gray-500 text-xs mt-1 italic">
                    dedicated to {c.dedicated_to}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                    c.payment_status === "planted" ? "bg-green-100 text-green-800" :
                    c.payment_status === "paid" || c.payment_status === "zelle_paid" || c.payment_status === "gpay_paid" ? "bg-blue-100 text-blue-800" :
                    "bg-yellow-100 text-yellow-800"
                  }`}>
                    {c.payment_status === "planted" ? "Planted" :
                     c.payment_status === "paid" || c.payment_status === "zelle_paid" || c.payment_status === "gpay_paid" ? "Paid" : "Pledged"}
                  </span>
                  {c.payment_status === "planted" && c.dedicated_to && (
                    <a
                      href={`https://sankalptaru.org/c/bpluswithbhaskar/?search_by=name&search=${encodeURIComponent(c.dedicated_to.trim().split(/[,&]/)[0].trim().split(/\s+/)[0])}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-[10px] text-green-700 hover:text-green-900 underline font-semibold"
                    >
                      View your plants
                    </a>
                  )}
                  <span className="text-gray-400 text-[10px] ml-auto">{formatDate(c.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(grouped)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([team, members]) => {
                const teamTotal = members.reduce(
                  (s, m) => s + (m.num_trees || 0),
                  0
                );
                return (
                  <div key={team}>
                    <div className="flex items-center gap-3 mb-4">
                      <h3 className="text-xl font-bold text-sritaru-700">
                        {team}
                      </h3>
                      <span className="bg-sritaru-600 text-white text-xs px-3 py-1 rounded-full">
                        {teamTotal.toLocaleString()} trees
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {members.map((c, i) => (
                        <div
                          key={i}
                          className="bg-white rounded-xl shadow-sm border border-sritaru-100 p-5 hover:shadow-md transition-shadow"
                        >
                          <p className="font-semibold text-sritaru-800 mb-1">
                            {c.first_name}
                          </p>
                          <p className="text-gray-700 text-sm">
                            contributed{" "}
                            <span className="font-bold text-sritaru-600">
                              {c.num_trees}
                            </span>{" "}
                            &#127795;
                          </p>
                          {c.dedicated_to && (
                            <p className="text-gray-500 text-xs mt-1 italic">
                              dedicated to {c.dedicated_to}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                              c.payment_status === "planted" ? "bg-green-100 text-green-800" :
                              c.payment_status === "paid" || c.payment_status === "zelle_paid" || c.payment_status === "gpay_paid" ? "bg-blue-100 text-blue-800" :
                              "bg-yellow-100 text-yellow-800"
                            }`}>
                              {c.payment_status === "planted" ? "Planted" :
                               c.payment_status === "paid" || c.payment_status === "zelle_paid" || c.payment_status === "gpay_paid" ? "Paid" : "Pledged"}
                            </span>
                            {c.payment_status === "planted" && c.dedicated_to && (
                              <a
                                href={`https://sankalptaru.org/c/bpluswithbhaskar/?search_by=name&search=${encodeURIComponent(c.dedicated_to.trim().split(/[,&]/)[0].trim().split(/\s+/)[0])}`}
                                target="_blank" rel="noopener noreferrer"
                                className="text-[10px] text-green-700 hover:text-green-900 underline font-semibold"
                              >
                                View your plants
                              </a>
                            )}
                            <span className="text-gray-400 text-[10px] ml-auto">{formatDate(c.created_at)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Wall;
